import { useState } from "react";
import { Card, Form, Input, Button, Typography, Row } from "antd";
import { CheckOutlined, CloseOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import CareHiresLogo from "../../assets/img/CH_New_Logo.png";
import Toaster, { showToast } from "../Toaster/Toaster";
import { setPassword } from "../../services/security.service";
import { passwordRules, validatePassword } from "../../utils/passwordRules";
import axios from "axios";

const { Title, Text } = Typography;

const SetPassword: React.FC = () => {
  const [form] = Form.useForm();
  const [password, setPasswordValue] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const token = new URLSearchParams(window.location.search).get("token") || "";

  const onFinish = async (values: { password: string }) => {
    if (!token) {
      showToast("Invalid or missing token.", { type: "error" });
      return;
    }
    setLoading(true);
    try {
      const res = await setPassword(token, values.password);
      if (res.success) {
        showToast("Password set successfully. Redirecting to login…", { type: "success" });
        setTimeout(() => navigate("/login"), 2000);
      } else {
        showToast("Failed to set password. The link may have expired.", { type: "error" });
      }
    } catch (error: unknown) {
      let msg = "There was an error processing your request. Please try again."
      if(axios.isAxiosError(error)) {
        msg = error.response?.data?.message || msg;
      }
      showToast(msg, { type: "error" });
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="forgot-password-container">
        <img className="forgot-password-logo" src={CareHiresLogo} alt="CareHires Logo" />
        <Row justify="center" align="middle">
          <Card className="forgot-password-card" style={{ maxWidth: 520, width: "100%" }}>
            <Title level={3} style={{ color: "#121451" }}>Invalid Link</Title>
            <Text>This password-set link is invalid or has expired. Please request a new one.</Text>
          </Card>
        </Row>
      </div>
    );
  }

  return (
    <div>
      <Toaster position="top-right" />
      <div className="forgot-password-container">
        <img className="forgot-password-logo" src={CareHiresLogo} alt="CareHires Logo" />
        <Row justify="center" align="middle">
          <Card className="forgot-password-card" style={{ maxWidth: 520, width: "100%" }}>
            <Title level={2} style={{ color: "#121451" }}>Set Your Password</Title>
            <Text style={{ display: "block", marginBottom: 20 }}>
              Choose a new password for your account.
            </Text>
            <Form
              form={form}
              onFinish={onFinish}
              layout="vertical"
              onValuesChange={(_, values) => setPasswordValue(values.password || "")}
            >
              <Form.Item name="password" label="New Password" rules={[{ validator: validatePassword }]}>
                <Input.Password placeholder="********" className="custom-input" />
              </Form.Item>

              <Form.Item
                name="confirmPassword"
                label="Confirm Password"
                dependencies={["password"]}
                rules={[
                  { required: true, message: "Please confirm your password!" },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue("password") === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject("Passwords do not match!");
                    },
                  }),
                ]}
              >
                <Input.Password placeholder="********" className="custom-input" />
              </Form.Item>

              <div className="password-requirements">
                {passwordRules.map((rule) => {
                  const passes = password ? rule.check(password) : false;
                  return (
                    <div key={rule.key} className="requirement-item">
                      {passes
                        ? <CheckOutlined className="valid-icon" />
                        : <CloseOutlined className="invalid-icon" />}
                      {rule.text}
                    </div>
                  );
                })}
              </div>

              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  block
                  loading={loading}
                  className="forgot-password-button"
                >
                  Set Password
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </Row>
      </div>    
    </div>
  );
};

export default SetPassword;
